import { HttpStatusCode } from "axios";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SERVICE_DETAIL_ITEM_TYPE } from "../../consts/serviceTemplate";

import { MemberShipCardInfo } from "../../types/memberShipCardTypes";

import { getServiceNameWithNewLine } from "../../libs/replace";
import { hideUnSupportService } from "../../libs/service";

import { useUserContext } from "../../contexts/userContext";

import { logout } from "../../services/authServices";
import {
  getTotalService,
  UserServiceResponse,
} from "../../services/userServiceServices";
import {
  getMemberShipInfo,
  navigateSmartOliveState,
} from "../../services/memberShipCardServices";

import useFullScreenLoading from "../../hooks/useFullScreenLoading";
import useNavigateService from "../../hooks/useNavigateService";
import useSimplePopup from "../../hooks/useSimplePopup";

import StickyHeader from "../../components/Layout/StickyHeader";
import ServiceBadge from "../../components/Badge/Service";

import MemberShipCardCI from "../../assets/img/member-ship-card.png";

type Props = {};

export type TotalServiceWrapper = UserServiceResponse & {
  subtitle?: string;
};

export default function TotalServices(props: Props) {
  const navigate = useNavigate();
  const [user] = useUserContext();
  const [services, setServices] = useState<TotalServiceWrapper[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [navigateService, navigateServiceComponent] = useNavigateService({});
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const [memberShip, setMemberShip] = useState<Partial<MemberShipCardInfo>>({});

  const handleNavigateSmartOlive = () => {
    navigate("/service/custom", {
      state: { iframeUrl: memberShip.url, ...navigateSmartOliveState },
    });
  };

  const handleNavigateService = (service: TotalServiceWrapper) => {
    if (
      [service.serviceDetailType, memberShip.serviceDetailType].every(
        (item) => item === SERVICE_DETAIL_ITEM_TYPE.SMART_OLIVE
      )
    ) {
      navigateService(service, undefined, undefined, handleNavigateSmartOlive);
    } else {
      navigateService(service);
    }
  };

  const getServiceIconUrl = (service: TotalServiceWrapper) => {
    if (
      [
        SERVICE_DETAIL_ITEM_TYPE.OUR_HOME,
        SERVICE_DETAIL_ITEM_TYPE.SMART_OLIVE,
      ].includes(service.serviceDetailType)
    ) {
      return MemberShipCardCI;
    } else {
      return service.fileInfo?.items?.filter((item) => item.scale === "full")[0]
        ?.url;
    }
  };

  useEffect(() => {
    if (!user.buildingId) return;
    setIsLoading(true);

    Promise.allSettled([getTotalService({}), getMemberShipInfo()])
      .then((ress) => {
        if (
          ress[0].status === "fulfilled" &&
          ress[0].value.status === HttpStatusCode.Ok
        ) {
          setServices(
            ress[0].value.data.filter((service) =>
              hideUnSupportService(user?.buildingId ?? "", service)
            )
          );
        }
        if (
          ress[1].status === "fulfilled" &&
          ress[1].value.status === HttpStatusCode.Ok
        ) {
          setMemberShip(ress[1].value.data);
        }
      })
      .catch((err) => {
        if (err.response.status === HttpStatusCode.Unauthorized) {
          logout();
        } else if (err?.response?.data?.message) {
          open?.(err?.response?.data?.message.replace(". ", ".\n"));
        } else {
          open?.("내정보 조회에 실패하였습니다.");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.buildingId, user.isLoading]);

  return (
    <div className="min-h-content">
      {loadingComponent}
      <StickyHeader
        title="전체 서비스"
        onBackClick={() => navigate("/view")}
        className="px-5"
      />
      <div className="px-5 pb-5">
        <div className="flex-col gap-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            {services.slice(0, 1).map((service) => (
              <ServiceBorderBox
                key={`${service.serviceTemplateId}${service.serviceId ?? ""}`}
                className="flex-1 h-[250px] relative text-lg font-semibold"
                onClick={() => handleNavigateService(service)}
              >
                <div className="absolute right-2 bottom-2">
                  <ImageOrBackgroundDiv
                    url={getServiceIconUrl(service)}
                    size="100px"
                    alt={service.serviceName}
                    service={service}
                    bgColor="#FAEDE4"
                  />
                </div>
                <div>{getServiceNameWithNewLine(service.serviceName)}</div>
                {service.subtitle && (
                  <div className="whitespace-pre-line text-xs text-[#A9AFB7]">
                    {service.subtitle}
                  </div>
                )}
              </ServiceBorderBox>
            ))}
            <div className="flex-1 flex flex-col gap-2.5">
              {services.slice(1, 3).map((service) => (
                <ServiceBorderBox
                  key={`${service.serviceTemplateId}${service.serviceId ?? ""}`}
                  className="h-[120px] relative"
                  onClick={() => handleNavigateService(service)}
                >
                  <div className="absolute right-2 bottom-2">
                    <ImageOrBackgroundDiv
                      url={getServiceIconUrl(service)}
                      size="60px"
                      alt={service.serviceName}
                      service={service}
                      bgColor="#F0EEFE"
                    />
                  </div>
                  <div className="whitespace-pre-line absolute">
                    {getServiceNameWithNewLine(service.serviceName)}
                  </div>
                  {service.subtitle && (
                    <div className="whitespace-pre-line text-xs text-[#A9AFB7]">
                      {service.subtitle}
                    </div>
                  )}
                </ServiceBorderBox>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2.5 pt-3">
            {services.slice(3, 6).map((service) => (
              <ServiceBorderBox
                key={`${service.serviceTemplateId}${service.serviceId ?? ""}`}
                className="h-[120px] relative text-sm flex items-end justify-center pb-2"
                onClick={() => handleNavigateService(service)}
              >
                <div className="absolute top-2 left-1/2 -translate-x-1/2">
                  <ImageOrBackgroundDiv
                    url={getServiceIconUrl(service)}
                    size="56px"
                    alt={service.serviceName}
                    service={service}
                  />
                </div>
                <div className="absolute top-[60%] z-10 text-sm text-center whitespace-pre-line h-[40px] flex items-center justify-center">
                  {getServiceNameWithNewLine(service.serviceName)}
                </div>
              </ServiceBorderBox>
            ))}
          </div>
        </div>
      </div>
      <div
        className="grid grid-cols-4 bg-brand-weather-badge-background gap-1 px-2"
        style={{ minHeight: "calc(100vh - 540px)" }}
      >
        {services.slice(6).map((service) => (
          <div
            key={`${service.serviceTemplateId}${service.serviceId ?? ""}`}
            className="h-[120px] flex flex-col items-center p-2 text-xs gap-2"
            onClick={() => handleNavigateService(service)}
          >
            <ImageOrBackgroundDiv
              url={getServiceIconUrl(service)}
              size="56px"
              alt={service.serviceName}
              service={service}
            />
            <div className="whitespace-pre-line text-center">
              {getServiceNameWithNewLine(service.serviceName)}
            </div>
          </div>
        ))}
      </div>

      {navigateServiceComponent}
      {simplePopupComponent}
    </div>
  );
}

export type ImageOrBackgroundDivProps = {
  url: string;
  size: string;
  alt: string;
  service?: TotalServiceWrapper;
  bgColor?: string;
};

export const ImageOrBackgroundDiv = ({
  url,
  size,
  alt,
  service,
  bgColor = "#F7F7F7",
}: ImageOrBackgroundDivProps) => {
  const sizeNumber = parseInt(size.replace("px", ""));

  return (
    <div className="relative">
      <div
        className={clsx([
          {
            "opacity-60":
              !service?.serviceId ||
              service.needAuthorized === true ||
              service.serviceStatus === "only-occupant",
          },
        ])}
      >
        {url ? (
          <div
            style={{
              width: sizeNumber + "px",
              height: sizeNumber + "px",
              backgroundColor: bgColor,
            }}
            className="rounded-full flex items-center justify-center"
          >
            <img
              src={url}
              alt={alt}
              className={clsx([" object-contain"])}
              style={{
                width:
                  sizeNumber === 56
                    ? sizeNumber - 20 + "px"
                    : sizeNumber + "px",
                height:
                  sizeNumber === 56
                    ? sizeNumber - 20 + "px"
                    : sizeNumber + "px",
              }}
            />
          </div>
        ) : (
          <div
            className="bg-slate-500 rounded-full opacity-60"
            style={{ width: size, height: size }}
          ></div>
        )}
      </div>
      <ServiceBadge
        service={service}
        className="min-w-[40px] left-1/2 -translate-x-1/2 bottom-0 absolute text-xs flex items-center justify-center whitespace-nowrap"
      />
    </div>
  );
};

export type ServiceBorderBoxProps = {
  children?: React.ReactNode;
  className?: string;
  onClick?: () => void;
};

export const ServiceBorderBox = ({
  children,
  className,
  onClick,
}: ServiceBorderBoxProps) => (
  <div
    className={clsx([
      "border border-brand-neutral-10 rounded-[10px] p-4 text-lg font-semibold whitespace-pre-line",
      className,
    ])}
    onClick={onClick}
  >
    {children}
  </div>
);
